import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { UserManager, WebStorageStateStore } from 'oidc-client-ts';
import { getAuthConfig, isAuthConfigured } from './authConfig';
import { clearContentCache } from '../content/contentApi';

const AuthContext = createContext({
  user: null,
  accessToken: null,
  isAuthenticated: false,
  loading: false,
  error: null,
  login: () => {},
  logout: () => {},
  dropSession: () => {}
});

const buildUserManager = () => {
  const { authority, clientId, hostedDomain } = getAuthConfig();
  const origin = window.location.origin;
  return new UserManager({
    authority,
    client_id: clientId,
    redirect_uri: `${origin}/`,
    post_logout_redirect_uri: `${origin}/`,
    response_type: 'code',
    scope: 'openid email profile',
    automaticSilentRenew: true,
    userStore: new WebStorageStateStore({ store: window.sessionStorage }),
    stateStore: new WebStorageStateStore({ store: window.sessionStorage }),
    metadata: {
      issuer: authority,
      authorization_endpoint: `${hostedDomain}/oauth2/authorize`,
      token_endpoint: `${hostedDomain}/oauth2/token`,
      userinfo_endpoint: `${hostedDomain}/oauth2/userInfo`,
      jwks_uri: `${authority}/.well-known/jwks.json`
    }
  });
};

const isAuthCallback = () => {
  const params = new URLSearchParams(window.location.search);
  return window.location.pathname === '/' && params.has('code') && params.has('state');
};

let userManager = null;
const getUserManager = () => {
  if (!userManager && isAuthConfigured()) userManager = buildUserManager();
  return userManager;
};

let callbackPromise = null;
const processCallbackOnce = (manager) => {
  if (!callbackPromise) callbackPromise = manager.signinCallback();
  return callbackPromise;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const manager = getUserManager();
    if (!manager) {
      setLoading(false);
      return undefined;
    }
    let alive = true;

    const handleUserLoaded = (loaded) => { if (alive) setUser(loaded); };
    const handleUserGone = () => { if (alive) setUser(null); };
    manager.events.addUserLoaded(handleUserLoaded);
    manager.events.addUserUnloaded(handleUserGone);
    manager.events.addAccessTokenExpired(handleUserGone);
    manager.events.addSilentRenewError(handleUserGone);

    const init = async () => {
      try {
        if (isAuthCallback()) {
          const signedIn = await processCallbackOnce(manager);
          const returnTo = (signedIn && signedIn.state && signedIn.state.returnTo) || '/';
          window.history.replaceState(null, '', returnTo);
          if (alive) setUser(signedIn || null);
        } else {
          const stored = await manager.getUser();
          if (alive) setUser(stored && !stored.expired ? stored : null);
        }
      } catch (callbackError) {
        window.history.replaceState(null, '', '/');
        if (alive) setError(callbackError);
      } finally {
        if (alive) setLoading(false);
      }
    };
    init();

    return () => {
      alive = false;
      manager.events.removeUserLoaded(handleUserLoaded);
      manager.events.removeUserUnloaded(handleUserGone);
      manager.events.removeAccessTokenExpired(handleUserGone);
      manager.events.removeSilentRenewError(handleUserGone);
    };
  }, []);

  const login = useCallback(async () => {
    const manager = getUserManager();
    if (!manager) {
      setError(new Error('auth_not_configured'));
      return;
    }
    setError(null);
    const { pathname, search, hash } = window.location;
    await manager.signinRedirect({ state: { returnTo: `${pathname}${search}${hash}` } });
  }, []);

  const dropSession = useCallback(async () => {
    const manager = getUserManager();
    if (manager) await manager.removeUser();
    clearContentCache();
    setUser(null);
  }, []);

  const logout = useCallback(async () => {
    const { clientId, hostedDomain } = getAuthConfig();
    await dropSession();
    const logoutUri = encodeURIComponent(`${window.location.origin}/`);
    window.location.assign(`${hostedDomain}/logout?client_id=${clientId}&logout_uri=${logoutUri}`);
  }, [dropSession]);

  const value = useMemo(() => ({
    user,
    accessToken: user && !user.expired ? user.access_token : null,
    isAuthenticated: Boolean(user && !user.expired),
    loading,
    error,
    login,
    logout,
    dropSession
  }), [user, loading, error, login, logout, dropSession]);

  if (loading && isAuthCallback()) return null;

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext);
